
// util/grouping.ts
import { ApiRow, GroupedUser, GroupedCurrency, GroupedState, GroupedCategory, StateName } from './interfaces/Review';

function parseIds(raw: string): number[] {
  // raw looks like "[184, 182]" or "[210]"
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr.map(Number).filter(n => !Number.isNaN(n));
  } catch (_) { /* ignore */ }
  return [];
}

const STATE_ORDER: StateName[] = [
  'submitted',
  'ready',
  'approved',
  'paid and booked',
  'denied',
  'rejected',
  'draft',
];

function stateOrderIndex(name: StateName): number {
  const idx = STATE_ORDER.indexOf(name);
  return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER; // unknown states at end
}


// util/grouping.ts (weiter)
export function groupByUserCurrencyState(rows: ApiRow[]): GroupedUser[] {
  // Defensive copy
  const data = Array.isArray(rows) ? rows : [];

  // Maps to accumulate
  const usersMap = new Map<number, {
    user: GroupedUser,
    currenciesMap: Map<string, {
      currency: GroupedCurrency,
      statesMap: Map<string, {
        state: GroupedState,
        categoriesMap: Map<number, GroupedCategory>
      }>
    }>
  }>();

  for (const r of data) {
    const userId = r.user_id;
    const currencyCode = r.currency;
    const currencyId = r.currency_id;
    const stateKey = r.state_name; // unique enough as key (name present everywhere)

    // Initialize user
    if (!usersMap.has(userId)) {
      usersMap.set(userId, {
        user: {
          userId,
          firstname: r.firstname,
          lastname: r.lastname,
          email: r.user_email,
          currencies: []
        },
        currenciesMap: new Map(),
      });
    }
    const u = usersMap.get(userId)!;

    // Initialize currency
    if (!u.currenciesMap.has(currencyCode)) {
      u.currenciesMap.set(currencyCode, {
        currency: {
          code: currencyCode,
          id: currencyId,
          states: []
        },
        statesMap: new Map(),
      });
    }
    const c = u.currenciesMap.get(currencyCode)!;

    // Initialize state
    if (!c.statesMap.has(stateKey)) {
      c.statesMap.set(stateKey, {
        state: {
          name: r.state_name,
          itemStateId: r.item_state_id,
          stateTotalAmount: parseFloat(r.state_total_amount),                // string → number
          stateTotalExchangeAmount: r.state_total_exchange_amount ?? null,   // number or null
          stateTotalIds: parseIds(r.state_total_ids),
          categories: []
        },
        categoriesMap: new Map(),
      });
    }
    const s = c.statesMap.get(stateKey)!;

    // Add/merge category
    const catId = r.item_category_id;
    if (!s.categoriesMap.has(catId)) {
      s.categoriesMap.set(catId, {
        id: catId,
        name: r.item_category_name,
        totalAmount: parseFloat(r.state_type_total_amount),
        totalExchangeAmount: r.state_type_total_exchange_amount ?? null,
        stateTypeIds: parseIds(r.state_type_ids),
      });
    } else {
      // If API could return multiple rows per category (shouldn't, but safe-guard), merge
      const existing = s.categoriesMap.get(catId)!;
      existing.totalAmount += parseFloat(r.state_type_total_amount);
      if (r.state_type_total_exchange_amount != null) {
        existing.totalExchangeAmount = (existing.totalExchangeAmount ?? 0) + r.state_type_total_exchange_amount;
      }
      existing.stateTypeIds = Array.from(new Set([...existing.stateTypeIds, ...parseIds(r.state_type_ids)]));
    }
  }

  // Materialize maps to arrays and sort states/categories deterministically
  const result: GroupedUser[] = [];

  for (const { user, currenciesMap } of usersMap.values()) {
    for (const { currency, statesMap } of currenciesMap.values()) {
      // states
      const statesArr: GroupedState[] = [];
      for (const { state, categoriesMap } of statesMap.values()) {
        // categories
        const categoriesArr = Array.from(categoriesMap.values())
          .sort((a, b) => a.name.localeCompare(b.name)); // alphabetical category order
        state.categories = categoriesArr;
        statesArr.push(state);
      }

      currency.states = statesArr.sort((a, b) => {
        const byOrder = stateOrderIndex(a.name) - stateOrderIndex(b.name);
        return byOrder !== 0 ? byOrder : a.name.localeCompare(b.name);
      });
    }

    user.currencies = Array.from(currenciesMap.values())
      .map(v => v.currency)
      .sort((a, b) => a.code.localeCompare(b.code)); // e.g. CHF, EUR, PHP, SGD

    result.push(user);
  }

  // Sort users by name (or keep incoming order)
  result.sort((a, b) => (a.firstname + a.lastname).localeCompare(b.firstname + b.lastname));
  return result;
}

export function groupFlatRowsByUser(rows: ApiRow[]): ApiRow[][] {
  const data: ApiRow[] = Array.isArray(rows) ? rows : [];

  const usersMap = new Map<number, {
    fullname: string;
    email: string;
    rows: ApiRow[];
  }>();

  for (const r of data) {
    const userId = Number(r.user_id);
    if (!usersMap.has(userId)) {
      const fullname = `${r.firstname ?? ''}${r.lastname ?? ''}`.trim();
      usersMap.set(userId, {
        fullname: fullname.length ? fullname : (r.user_email ?? ''),
        email: r.user_email ?? '',
        rows: [r],
      });
    } else {
      usersMap.get(userId)!.rows.push(r);
    }
  }

  const byDateDesc = (a: ApiRow, b: ApiRow) => {
    const ta = new Date(a.date).getTime();
    const tb = new Date(b.date).getTime();
    return Number.isFinite(tb - ta) ? (tb - ta) : 0;
  };

  const result: ApiRow[][] = Array
    .from(usersMap.values())
    .sort((a, b) => a.fullname.localeCompare(b.fullname))
    .map(u => u.rows.sort(byDateDesc));

  return result;
}
